import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useHistory } from 'react-router-dom';
import { Typography } from 'seagull';
import { useEventReporter, useOverlayContext, useStoreActions, useStoreStatic } from "../../contexts";
import { redirect, routes } from "../../constants";
import { useOnMount } from "../../hooks/useOnMount";
import { AnchorLogo } from 'seagull/icons';
import { notificationRouter } from "./notificationRouter";
import { ErrorResponse, ServerError } from "../../infra/ErrorResponse";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import styles from "./Notification.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const acceptNotificationLoader = 'acceptNotification';
const invalidInvitationError = 'invalidInvitation';
function InvalidInvitationIndicator() {
  return /*#__PURE__*/_jsx("div", {
    className: styles.invalidWrapper,
    "data-testid": "invalid-invitation",
    children: /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(AnchorLogo, {
        className: styles.logo
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h3",
        weight: "normal",
        children: "This invitation link is no longer valid"
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h5",
        children: "If you would like to join the account, we recommend contacting the person who invited you."
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h5",
        children: "For support, please email support@sayanchor.com."
      })]
    })
  });
}
export default function Notification(props) {
  const {
    notificationId
  } = props.match.params;
  const [error, setError] = useState();
  const {
    reportToHeap,
    reportToGtm,
    reportToAppcues,
    reportToHubspot
  } = useEventReporter();
  const {
    getState
  } = useStoreStatic();
  const {
    api,
    setAuthToken
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const location = useLocation();
  const history = useHistory();
  const featureFlags = useFeatureFlags();
  useOnMount(() => {
    async function handleAuthenticationAndRedirect() {
      const isLoggedIn = !!getState().authToken;
      overlayContext.startLoader(acceptNotificationLoader);
      let redirectToUrl, stateChanges;
      try {
        const searchParams = new URLSearchParams(location.search);
        const intent = searchParams.get('intent') || undefined;
        const notification = await (isLoggedIn ? api.notification.getWhileLoggedIn(notificationId, intent) : api.notification.getWhileLoggedOut(notificationId, intent));
        if (notification.authToken != null) await setAuthToken(notification.authToken);
        if (notification && notification.notificationType) {
          reportToGtm('notification-open', notification.notificationType);
          reportToAppcues('notification-open', notification.notificationType);
          reportToHeap('notification', 'open', notification.notificationType);
        }
        const {
          redirect,
          error,
          hubspotEventName
        } = notificationRouter({
          notification,
          claims: getState().claims,
          userInfo: getState().userInfo,
          featureFlags
        });
        hubspotEventName && reportToHubspot(hubspotEventName);
        redirectToUrl = redirect.url;
        stateChanges = redirect.state;
        if (error) {
          const err = new Error(error + " - type:".concat(notification.type || '(empty)', ";notificationType:").concat(notification.notificationType || '(empty)'));
          Sentry.withScope(scope => {
            scope.setContext('Notification data', {
              notificationPayload: JSON.stringify(notification)
            });
            Sentry.captureException(err);
          });
          overlayContext.showErrorToast("Oops! We couldn't find the correct page. Please contact support@sayanchor.com and let us know.", 'notification-redirect-toast');
        }
      } catch (err) {
        const e = new ErrorResponse(err);
        if (e.status === 401) {
          if (isLoggedIn) {
            setAuthToken(null);
          }
          const loginFlow = e.getLoginFlowData();
          if (loginFlow) {
            stateChanges = {
              setLoginFlow: loginFlow
            };
          }
          redirectToUrl = redirect.getPathAndSearch({
            returnTo: location.pathname
          });
        } else if (e.status === 403) {
          const recipientEmail = e.getRecipientEmail();
          const sourceUrl = location.pathname;
          const notificationNoAccessState = {
            recipientEmail: recipientEmail,
            returnTo: sourceUrl
          };
          stateChanges = notificationNoAccessState;
          redirectToUrl = routes.noAccessPage;
        } else if (e.hasServerError(ServerError.RecipientNotFound) && isLoggedIn) {
          // logout flow will trigger an unmount/mount cycle because this component renders only when authFlowDone is true.
          await setAuthToken(null);
        } else if (e.hasServerError(ServerError.InvalidTeamInvitation)) {
          setError(invalidInvitationError);
        } else {
          redirectToUrl = routes.home;
        }
      }
      if (redirectToUrl) {
        history.replace(redirectToUrl, stateChanges);
      }
      overlayContext.stopLoader(acceptNotificationLoader);
    }
    handleAuthenticationAndRedirect();
  });
  return error === invalidInvitationError ? /*#__PURE__*/_jsx(InvalidInvitationIndicator, {}) : null;
}